import React, { useState } from "react";
import { ListChecks, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { formatWhen } from "./ssoFormat";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";

function TemplateDialog({ editing, onClose, onSaved }) {
  const [title, setTitle] = useState(editing?.title || "");
  const [steps, setSteps] = useState((editing?.steps || []).join("\n"));
  const [busy, setBusy] = useState(false);
  const lines = steps.split("\n").map((line) => line.trim()).filter(Boolean);

  const save = async () => {
    setBusy(true);
    try {
      const body = { title: title.trim(), steps: lines };
      if (editing?.id) await api.put(`/admin/sso/templates/${editing.id}`, body);
      else await api.post("/admin/sso/templates", body);
      toast.success("Template saved");
      onSaved();
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="template-dialog">
        <DialogHeader>
          <DialogTitle>{editing?.id ? "Edit rotation template" : "New rotation template"}</DialogTitle>
          <DialogDescription>One step per line, up to 20. Rotations already staged keep the steps they started with.</DialogDescription>
        </DialogHeader>
        <label className="block mt-3"><span className="field-label">Title</span>
          <input className="input-flat" maxLength={80} value={title} onChange={(e) => setTitle(e.target.value)} data-testid="template-title" /></label>
        <label className="block mt-4"><span className="field-label">Steps</span>
          <textarea className="input-flat font-mono text-xs" rows={9} value={steps} onChange={(e) => setSteps(e.target.value)} data-testid="template-steps" /></label>
        <DialogFooter className="mt-4">
          <button className="btn-ghost" onClick={onClose} data-testid="template-cancel">Cancel</button>
          <button className="btn-primary" disabled={busy || title.trim().length < 3 || !lines.length || lines.length > 20} onClick={save} data-testid="template-save">
            {busy ? "Saving…" : "Save template"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function RotationTemplatesCard({ templates, onChanged, readOnly }) {
  const [editing, setEditing] = useState(null);
  const remove = async (template) => {
    if (!window.confirm(`Delete the "${template.title}" template? Rotations that used it keep their steps.`)) return;
    try { await api.delete(`/admin/sso/templates/${template.id}`); toast.success("Template deleted"); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <section className="border p-6" style={{ borderColor: "var(--border)", background: "var(--surface)" }} data-testid="templates-card">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
        <div>
          <div className="eyebrow mb-1 flex items-center gap-1.5"><ListChecks size={11} /> Rotation templates</div>
          <h3 className="font-heading font-bold text-lg" style={{ color: "var(--ink)" }}>Reusable rotation steps</h3>
        </div>
        {!readOnly && <button className="btn-ghost flex items-center gap-2 text-sm" onClick={() => setEditing({})} data-testid="template-create"><Plus size={14} /> New template</button>}
      </div>
      {templates.map((template) => (
        <details key={template.id} className="py-3 border-t" style={{ borderColor: "var(--border)" }} data-testid={`template-row-${template.id}`}>
          <summary className="flex flex-wrap items-center gap-3 cursor-pointer text-sm list-none">
            <span className="font-medium" style={{ color: "var(--ink)" }}>{template.title}</span>
            <span className="text-xs" style={{ color: "var(--muted)" }}>{template.steps.length} steps · updated {formatWhen(template.updated_at)} by {template.updated_by}</span>
            {!readOnly && (
              <span className="ml-auto flex gap-1.5">
                <button className="btn-ghost !py-1 !px-2 text-xs flex items-center gap-1" onClick={(e) => { e.preventDefault(); setEditing(template); }} data-testid={`template-edit-${template.id}`}><Pencil size={12} /> Edit</button>
                <button className="btn-ghost !py-1 !px-2 text-xs flex items-center gap-1" onClick={(e) => { e.preventDefault(); remove(template); }} data-testid={`template-delete-${template.id}`}><Trash2 size={12} /> Delete</button>
              </span>
            )}
          </summary>
          <ol className="list-decimal pl-5 mt-2 space-y-1 text-xs" style={{ color: "var(--muted)" }}>
            {template.steps.map((step, index) => <li key={`${template.id}-${index}`}>{step}</li>)}
          </ol>
        </details>
      ))}
      {!templates.length && <p className="text-sm" style={{ color: "var(--muted)" }} data-testid="templates-empty">No templates yet.</p>}
      {editing && <TemplateDialog editing={editing} onClose={() => setEditing(null)} onSaved={onChanged} />}
    </section>
  );
}
